import React, { useState, useEffect } from 'react';
import { Player, GameState } from '../../domain/entities';
import { Modal } from './Modal';
import { PlayerFace } from './PlayerFace';
import { View } from '../../App';
import { AiService } from '../../services/AiService';

interface PlayerConversationModalProps {
    player: Player;
    onClose: () => void;
    onAction: (playerId: number, choice: 'praise' | 'promise' | 'show_faith' | 'demand_improvement' | 'warn_dropped' | 'dismiss_concerns' | 'assert_authority') => Promise<{ success: boolean; message: string; }>;
    onOfferFinancialIncentive: (playerId: number, type: 'wage' | 'bonus', amount: number) => Promise<{ success: boolean; message: string; }>;
    gameState: GameState;
    pushView: (view: View) => void;
    // Add isAiPunditEnabled to props to control AI usage
    isAiPunditEnabled: boolean;
    canTalk: boolean;
    cooldown: GameState['managerTalkCooldownEnd'] | { season: number; week: number; } | undefined;
    weeklyTalkLimitReached?: boolean;
}

const getRatingColor = (rating: number): string => {
    if (rating >= 8) return 'var(--positive-color)';
    if (rating >= 5) return 'var(--info-color)';
    return 'var(--negative-color)';
};

export const PlayerConversationModal: React.FC<PlayerConversationModalProps> = ({ player, onClose, onAction, onOfferFinancialIncentive, gameState, pushView, isAiPunditEnabled, canTalk, cooldown, weeklyTalkLimitReached }) => {
    const [result, setResult] = useState<{ message: string; success: boolean } | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [modalView, setModalView] = useState<'main' | 'positive' | 'negative' | 'finance' | 'cooldown'>(!canTalk ? 'cooldown' : 'main');
    const [openingDialogue, setOpeningDialogue] = useState<string | null>(null);
    const [isDialogueLoading, setIsDialogueLoading] = useState(true);

    const playerTeam = gameState.leagues[gameState.playerLeagueName].teams.find(t => t.name === gameState.playerTeamName);

    const playerTeamColor = React.useMemo(() => {
        if (playerTeam && playerTeam.squad.some(p => p.id === player.id)) {
            return playerTeam.colors.primary;
        }
        // Look for the player's team in case it's not the player's team
        for (const league of Object.values(gameState.leagues)) {
            for (const team of league.teams) {
                if (team.squad.some(p => p.id === player.id)) {
                    return team.colors.primary;
                }
            }
        }
        return undefined;
    }, [player.id, playerTeam, gameState.leagues]);

    useEffect(() => {
        const fetchDialogue = async () => {
            if (!playerTeam) return;
            setIsDialogueLoading(true);

            const hasIssue = player.moraleReason;

            if (isAiPunditEnabled) {
                try {
                    const dialogue = await AiService.generateOpeningDialogue(player, playerTeam);
                    setOpeningDialogue(dialogue);
                } catch (error) {
                    console.error("Failed to generate AI dialogue, using fallback:", error);
                    setOpeningDialogue(hasIssue
                        ? `"Gaffer, we need to talk about my situation: ${player.moraleReason}"`
                        : `"Gaffer, I appreciate your time. Just wanted to check in with you."`
                    );
                }
            } else {
                // Offline fallback dialogue
                setOpeningDialogue(hasIssue
                    ? `"Gaffer, can we have a word? I'm concerned about ${player.moraleReason?.toLowerCase()}."`
                    : `"Gaffer, I appreciate your time. Just wanted to check in with you."`
                );
            }
            setIsDialogueLoading(false);
        };
        fetchDialogue();
    }, [player, playerTeam, isAiPunditEnabled]);

    const handleChoice = async (choice: 'praise' | 'promise' | 'show_faith' | 'demand_improvement' | 'warn_dropped' | 'dismiss_concerns' | 'assert_authority') => {
        setIsSubmitting(true);
        const res = await onAction(player.id, choice);
        setResult(res);
        setIsSubmitting(false);
    };

    const handleFinancialChoice = async (type: 'wage' | 'bonus', multiplierOrAmount: number) => {
        setIsSubmitting(true);
        let amount: number;
        if (type === 'wage') {
            amount = Math.round(player.wage * multiplierOrAmount);
        } else {
            amount = multiplierOrAmount;
        }

        const res = await onOfferFinancialIncentive(player.id, type, amount);
        setResult(res);
        setIsSubmitting(false);
    };

    const handlePlayerLinkClick = () => {
        onClose();
        pushView({ type: 'playerDetail', title: `Player: ${player.name}`, playerId: player.id });
    };
    
    const isPerformanceIssue = player.moraleReason === 'Poor recent performance';
    const teamBalance = playerTeam?.finances.balance || 0;

    const wageOptions = [
        { label: '+10%', multiplier: 1.1 },
        { label: '+25%', multiplier: 1.25 },
        { label: '+50%', multiplier: 1.5 },
    ];
    const bonusOptions = [
        { label: 'Small', amount: player.wage * 4 },
        { label: 'Medium', amount: player.wage * 8 },
        { label: 'Large', amount: player.wage * 12 },
    ];

    const renderCooldownView = () => {
        const isWeeklyLimit = weeklyTalkLimitReached;

        return (
            <>
                {isWeeklyLimit ? (
                    <p style={{ color: 'var(--warning-color)', marginBottom: '16px' }}>
                        You've already spoken to 2 players this week. You can't have any more conversations until next week.
                    </p>
                ) : (
                    <>
                        <p style={{ color: 'var(--warning-color)', marginBottom: '16px' }}>
                            You recently spoke to {player.name}. You need to give them some time before having another conversation.
                        </p>
                        <p style={{ marginBottom: '16px' }}>
                            <strong>Available again after:</strong> S{cooldown?.season} W{Math.floor(cooldown?.week || 0)}
                        </p>
                    </>
                )}
                <div className="modal-actions">
                    <button className="button" onClick={onClose}>Close</button>
                </div>
            </>
        );
    };

    const renderMainView = () => (
        <>
            {isDialogueLoading ? (
                <p><i>Thinking...</i></p>
            ) : (
                <p><i>"{openingDialogue}"</i></p>
            )}
            
            {isPerformanceIssue && player.recentRatings && player.recentRatings.length > 0 && (
                <div className="recent-ratings-display">
                    <span>Recent Form:</span>
                    <div className="ratings-list" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {[...player.recentRatings].reverse().map((rating, index) => (
                            <span key={index} className="rating-badge" style={{ backgroundColor: getRatingColor(rating), color: '#fff' }}>
                                {rating.toFixed(1)}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            <p style={{marginTop: '16px'}}>How do you want to handle this?</p>

            <div className="modal-actions" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                <button className="button" onClick={() => setModalView('positive')} disabled={isSubmitting}>
                    Positive Approach 🙂
                </button>
                <button className="button" onClick={() => setModalView('negative')} disabled={isSubmitting}>
                    Tough Approach 😠
                </button>
                <button className="button" onClick={() => setModalView('finance')} disabled={isSubmitting || isPerformanceIssue} title={isPerformanceIssue ? "Financial incentives won't solve poor form." : "Offer a financial incentive"}>
                    Offer Financial Incentive 💰
                </button>
            </div>
        </>
    );

    const renderPositiveView = () => (
        <>
            <h4>Positive Approach</h4>
            <p>Try to boost the player's confidence and morale through encouragement and promises.</p>
            <div className="modal-actions" style={{ flexDirection: 'column', alignItems: 'stretch', marginTop: '24px' }}>
                {isPerformanceIssue ? (
                    <button className="button" onClick={() => handleChoice('show_faith')} disabled={isSubmitting}>
                        Show faith in their ability
                    </button>
                ) : (
                    <>
                        <button className="button" onClick={() => handleChoice('promise')} disabled={isSubmitting}>
                            Promise more game time
                        </button>
                        <button className="button" onClick={() => handleChoice('praise')} disabled={isSubmitting}>
                            Offer praise & encouragement
                        </button>
                    </>
                )}
                <button className="button secondary" style={{marginTop: '8px'}} onClick={() => setModalView('main')} disabled={isSubmitting}>Back</button>
            </div>
        </>
    );

    const renderNegativeView = () => (
        <>
            <h4>Tough Approach</h4>
            <p>Challenge the player to improve their performance or change their attitude. This can be risky.</p>
            <div className="modal-actions" style={{ flexDirection: 'column', alignItems: 'stretch', marginTop: '24px' }}>
                {isPerformanceIssue ? (
                    <>
                        <button className="button" onClick={() => handleChoice('demand_improvement')} disabled={isSubmitting}>
                            Demand they work harder
                        </button>
                        <button className="button danger" onClick={() => handleChoice('warn_dropped')} disabled={isSubmitting}>
                            Warn them they could be dropped
                        </button>
                    </>
                ) : (
                    <>
                        <button className="button" onClick={() => handleChoice('dismiss_concerns')} disabled={isSubmitting}>
                            Tell them to focus on their football
                        </button>
                        <button className="button danger" onClick={() => handleChoice('assert_authority')} disabled={isSubmitting}>
                            Remind them nobody is bigger than the club
                        </button>
                    </>
                )}
                <button className="button secondary" style={{marginTop: '8px'}} onClick={() => setModalView('main')} disabled={isSubmitting}>Back</button>
            </div>
        </>
    );

    const renderFinanceView = () => (
        <div className="finance-options-container">
            <h4>Offer Financial Incentive</h4>
            <div className="balance-display">
                Available Funds: <strong className={teamBalance > 0 ? 'positive' : 'negative'}>£{teamBalance.toLocaleString()}</strong>
            </div>
            
            <div className="negotiation-group">
                <h5>Increase Weekly Wage (Permanent)</h5>
                <p>Current: £{player.wage.toLocaleString()}/wk</p>
                <div className="negotiation-options">
                    {wageOptions.map(opt => {
                        const newWage = Math.round(player.wage * opt.multiplier);
                        return (
                            <button key={opt.label} className="button" onClick={() => handleFinancialChoice('wage', opt.multiplier)} disabled={isSubmitting}>
                                {opt.label} (£{newWage.toLocaleString()}/wk)
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="negotiation-group">
                <h5>Offer One-Off Bonus</h5>
                <div className="negotiation-options">
                    {bonusOptions.map(opt => (
                        <button key={opt.label} className="button" onClick={() => handleFinancialChoice('bonus', opt.amount)} disabled={isSubmitting || teamBalance < opt.amount}>
                            {opt.label} (£{opt.amount.toLocaleString()})
                        </button>
                    ))}
                </div>
            </div>

            <div className="modal-actions">
                <button className="button secondary" onClick={() => setModalView('main')} disabled={isSubmitting}>Back</button>
            </div>
        </div>
    );

    const renderResultView = () => (
         <>
            <p>
                {result!.success ? '👍 ' : '👎 '}
                {result!.message}
            </p>
            <div className="modal-actions">
                <button className="button" onClick={onClose}>Close</button>
            </div>
        </>
    );

    const renderContent = () => {
        if (result) {
            return renderResultView();
        }
        switch (modalView) {
            case 'cooldown': return renderCooldownView();
            case 'positive': return renderPositiveView();
            case 'negative': return renderNegativeView();
            case 'finance': return renderFinanceView();
            case 'main':
            default:
                return renderMainView();
        }
    };

    return (
        <Modal isOpen={true} onClose={onClose}>
            <h2>Talk to Player</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', margin: '16px 0' }}>
                <PlayerFace player={player} teamColor={playerTeamColor} />
                <div>
                    <h3 style={{ margin: 0 }}>
                         <button className="link-style" onClick={handlePlayerLinkClick} title={`View ${player.name}'s profile`}>
                            {player.name}
                        </button>
                    </h3>
                    <p style={{ margin: 0 }}>Morale: <span className={player.morale === 'Furious' || player.morale === 'Unhappy' ? 'negative' : 'info'}>{player.morale}</span></p>
                </div>
            </div>
            
            {renderContent()}
        </Modal>
    );
};